// Vario-Slot: account.sidebar
//
// Zeigt pro Vario-Geschäftsbeziehung (account.types) eine eigene Export-Sektion:
//  - CUSTOMER     → „Als Kunde": Status + Button (RPC gambio_enqueue_single_customer_export)
//  - MANUFACTURER → „Als Hersteller": Status + Button (RPC gambio_enqueue_single_manufacturer_export)
// Ein Account kann beide Typen tragen (dann beide Sektionen). Der Hersteller-Status kommt aus
// der Edge-Function (EAV custom.gambioManufacturer.id ODER Namensabgleich gegen Gambio /manufacturers).
//
// URL-Params (Vario): id (=accountId). Kein salesChannelId / listingId - der Slot
// accounts.detail.tabs.masterdata.index liefert nur die Account-ID.
// Die View ermittelt die Gambio-Shops + deren salesChannelId selbst via gambio_list_shops.
// Inline-Slot mit AUTO-Höhe (initVarioIframeHeight('auto') in main.ts).

import type { SupabaseClient } from '@supabase/supabase-js';
import { showToast } from '../lib/toast.js';
import { openErrorModal } from '@vario-connect/frontend-design';
import { awaitJob } from '../lib/await-job.js';
import { helpButtonHtml, wireHelpButtons } from '../lib/help-panel.js';

interface SlotCtx {
  contentEl: HTMLElement;
  supabase: SupabaseClient;
  session: { token: string; tenantId: string; marketplaceKind: string };
  supabaseUrl: string;
}

interface ExportStatus {
  linked: boolean;
  gambioCustomerId: number | null;
  gambioNumber: string | null;
  displayName: string | null;
  // Geschäftsbeziehungen (Vario account.types) + Hersteller-Status. Optional, damit die View
  // auch gegen eine ältere Edge-Function-Version nicht bricht.
  types?: string[];
  isCustomer?: boolean;
  isManufacturer?: boolean;
  customerLinked?: boolean;
  manufacturerName?: string | null;
  manufacturerLinked?: boolean;
  gambioManufacturerId?: number | null;
}

interface ShopRow {
  shop_key: string;
  label: string | null;
  status: string;
  vario_channel: { id?: string; label?: string } | null;
}

export async function renderAccountCustomerExport(ctx: SlotCtx): Promise<void> {
  ctx.contentEl.hidden = false;

  // Inline-Kunden-Slot mit AUTO-Höhe: html/body-Kette für Grow-to-fit aufbrechen.
  document.documentElement.classList.add('go-lister-autogrow');

  ctx.contentEl.innerHTML = renderShell();

  wireHelpButtons(ctx.contentEl);

  const params = readSlotParams();
  if (!params.accountId) {
    const body = ctx.contentEl.querySelector<HTMLElement>('#slot-body')!;
    body.innerHTML = `
      <div class="v-banner v-banner-warn">
        <span class="v-icon fa-light fa-triangle-exclamation"></span>
        <div>URL-Parameter unvollständig: accountId fehlt.</div>
      </div>`;
    return;
  }

  const accountId = params.accountId;

  // Gambio-Shops laden
  let shops: ShopRow[] = [];
  {
    const { data, error } = await ctx.supabase.rpc('gambio_list_shops');
    if (error) {
      const body = ctx.contentEl.querySelector<HTMLElement>('#slot-body')!;
      body.innerHTML = `
        <div class="v-banner v-banner-danger">
          <span class="v-icon fa-light fa-circle-xmark"></span>
          <div>Gambio-Shops konnten nicht geladen werden: ${escapeHtml(error.message)}</div>
        </div>`;
      return;
    }
    shops = ((data ?? []) as ShopRow[]).filter((s) => s.vario_channel?.id);
  }

  if (shops.length === 0) {
    const body = ctx.contentEl.querySelector<HTMLElement>('#slot-body')!;
    body.innerHTML = `
      <div class="v-banner v-banner-warn">
        <span class="v-icon fa-light fa-triangle-exclamation"></span>
        <div>Kein Gambio-Verkaufskanal konfiguriert.</div>
      </div>`;
    return;
  }

  await renderStatus();

  async function renderStatus(): Promise<void> {
    setStatusBar(ctx.contentEl, 'loading', 'Lade Export-Status…');
    const body = ctx.contentEl.querySelector<HTMLElement>('#slot-body')!;

    let status: ExportStatus;
    try {
      const res = await fetch(`${ctx.supabaseUrl}/functions/v1/gambio-account-export-status`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${ctx.session.token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ accountId }),
      });
      const json = (await res.json().catch(() => ({}))) as ExportStatus & { error?: { message: string } };
      if (!res.ok) {
        const msg = (json as { error?: { message: string } }).error?.message ?? `HTTP ${res.status}`;
        setStatusBar(ctx.contentEl, 'error', 'Fehler');
        body.innerHTML = `
          <div class="v-banner v-banner-danger" style="margin-bottom:8px;">
            <span class="v-icon fa-light fa-circle-xmark"></span>
            <div>
              <div>${escapeHtml(msg)}</div>
              <button type="button" id="retry-btn" class="v-button v-button--ghost" style="margin-top:6px;">
                <span class="v-icon fa-regular fa-rotate-right"></span> Erneut versuchen
              </button>
            </div>
          </div>`;
        body.querySelector<HTMLButtonElement>('#retry-btn')!.addEventListener('click', () => void renderStatus());
        return;
      }
      status = json;
    } catch (e) {
      const msg = (e as Error).message;
      setStatusBar(ctx.contentEl, 'error', 'Fehler');
      body.innerHTML = `
        <div class="v-banner v-banner-danger" style="margin-bottom:8px;">
          <span class="v-icon fa-light fa-circle-xmark"></span>
          <div>
            <div>${escapeHtml(msg)}</div>
            <button type="button" id="retry-btn" class="v-button v-button--ghost" style="margin-top:6px;">
              <span class="v-icon fa-regular fa-rotate-right"></span> Erneut versuchen
            </button>
          </div>
        </div>`;
      body.querySelector<HTMLButtonElement>('#retry-btn')!.addEventListener('click', () => void renderStatus());
      return;
    }

    setStatusBar(ctx.contentEl, '', '');
    renderStatusView(body, status);
  }

  function renderStatusView(body: HTMLElement, status: ExportStatus): void {
    const multipleShops = shops.length > 1;

    // Welche Sektionen werden gezeigt? Gate auf die Vario-Geschäftsbeziehung (account.types).
    // Fallback: antwortet eine ältere Edge-Function ohne types-Felder, verhält sich die View
    // wie zuvor (nur Kunde). Ein bereits verknüpfter Kunde/Hersteller wird nie versteckt.
    const showCustomer = status.isCustomer === true || status.customerLinked === true || status.linked === true
      || (status.isCustomer === undefined && status.isManufacturer === undefined);
    const showManufacturer = status.isManufacturer === true || status.manufacturerLinked === true;

    const sections: string[] = [];
    if (showCustomer) sections.push(sectionHtml('customer', status, multipleShops));
    if (showManufacturer) sections.push(sectionHtml('manufacturer', status, multipleShops));

    if (sections.length === 0) {
      body.innerHTML = `
        <div class="v-banner v-banner-info">
          <span class="v-icon fa-light fa-circle-info"></span>
          <div>Dieser Account hat weder die Geschäftsbeziehung „Kunde" noch „Hersteller" - kein Gambio-Export möglich.</div>
        </div>`;
      return;
    }

    body.innerHTML = sections.join('<div style="height:14px;"></div>');

    body.querySelectorAll<HTMLButtonElement>('button[data-sales-channel-id]').forEach((btn) => {
      btn.addEventListener('click', () => {
        const scId = btn.dataset.salesChannelId!;
        const kind = btn.dataset.kind === 'manufacturer' ? 'manufacturer' : 'customer';
        void handleExport(btn, scId, kind);
      });
    });
  }

  /** Baut eine Export-Sektion (Kunde ODER Hersteller): Überschrift + Status-Banner + Buttons pro Shop. */
  function sectionHtml(kind: 'customer' | 'manufacturer', status: ExportStatus, multipleShops: boolean): string {
    const isCustomer = kind === 'customer';
    const heading = isCustomer ? 'Als Kunde' : 'Als Hersteller';
    const icon = isCustomer ? 'fa-user' : 'fa-industry';
    const entity = isCustomer ? 'Kunde' : 'Hersteller';
    const linked = isCustomer ? (status.customerLinked ?? status.linked) : (status.manufacturerLinked ?? false);
    const gambioId = isCustomer ? status.gambioCustomerId : (status.gambioManufacturerId ?? null);
    const idText = gambioId != null ? ` #${escapeHtml(String(gambioId))}` : '';
    const nameText = isCustomer
      ? (status.displayName ? ` - ${escapeHtml(status.displayName)}` : '')
      : (status.manufacturerName ? ` - ${escapeHtml(status.manufacturerName)}` : '');

    const banner = linked
      ? `<div class="v-banner v-banner-success" style="margin-bottom:10px;">
           <span class="v-icon fa-light fa-circle-check"></span>
           <div>Bereits als Gambio-${entity}${idText}${nameText} angelegt</div>
         </div>`
      : `<div class="v-banner v-banner-info" style="margin-bottom:10px;">
           <span class="v-icon fa-light fa-circle-info"></span>
           <div>Noch nicht als ${entity} in Gambio angelegt.</div>
         </div>`;

    const buttons = shops.map((shop) => {
      const salesChannelId = shop.vario_channel!.id!;
      const shopName = shop.label ?? shop.shop_key;
      const verb = linked ? 'In Gambio aktualisieren' : 'In Gambio anlegen';
      const label = multipleShops ? `${verb} (${escapeHtml(shopName)})` : verb;
      return `
        <button type="button"
          class="v-button v-button--primary"
          data-kind="${kind}"
          data-shop-key="${escapeHtml(shop.shop_key)}"
          data-sales-channel-id="${escapeHtml(salesChannelId)}"
          style="margin-bottom:4px;">
          <span class="v-icon fa-light fa-arrow-up-from-bracket"></span>
          ${label}
        </button>`;
    }).join('');

    return `
      <section>
        <div style="font-size:12px;font-weight:600;letter-spacing:.04em;color:var(--muted);text-transform:uppercase;margin-bottom:6px;">
          <span class="v-icon fa-light ${icon}"></span> ${heading}
        </div>
        ${banner}
        <div style="display:flex;flex-direction:column;align-items:flex-start;gap:4px;">
          ${buttons}
        </div>
      </section>`;
  }

  async function handleExport(btn: HTMLButtonElement, salesChannelId: string, kind: 'customer' | 'manufacturer'): Promise<void> {
    btn.disabled = true;

    const rpc = kind === 'manufacturer'
      ? 'gambio_enqueue_single_manufacturer_export'
      : 'gambio_enqueue_single_customer_export';
    const { data, error } = await ctx.supabase.rpc(rpc, {
      _account_id: accountId,
      _sales_channel_id: salesChannelId,
    });

    btn.disabled = false;

    if (error) {
      openErrorModal({
        title: 'Export fehlgeschlagen',
        items: [{ message: error.message }],
        context: { action: rpc, accountId, salesChannelId, kind },
        json: error,
      });
      return;
    }

    const rows = Array.isArray(data) ? data : [];
    const reused = rows[0]?.reused === true;
    const jobId = typeof rows[0]?.job_id === 'string' ? (rows[0].job_id as string) : null;
    showToast(reused ? 'Export läuft bereits' : 'Export gestartet', 'info');

    // Auf das Job-Ergebnis warten, damit fachliche Abweisungen hier sichtbar werden und nicht nur
    // im Log-Tab landen. Zwei Fälle: das Ziel ist ein Gambio-Administrator-Konto (Sicherheits-Guard)
    // oder der Kunde ist keiner Gambio-Kundengruppe zugeordnet. Beide weist der Worker über
    // `result.rejected` ab statt zu werfen — es sind also KEINE failed-Jobs, `status` bleibt
    // `completed`. Wer nur auf `failed` prüft, übersieht sie.
    if (jobId) {
      btn.disabled = true;
      try {
        const job = await awaitJob(ctx.supabase, jobId, 60_000);
        interface ExportJobResult {
          rejected?: string;
          varioCustomerGroupLabel?: string | null;
          varioPriceGroupLabel?: string | null;
          gambioCustomerId?: number | null;
          gambioStatusId?: number | null;
        }
        const result: ExportJobResult = (job.result as ExportJobResult | null) ?? {};

        if (result.rejected === 'gambio-protected-account') {
          openErrorModal({
            title: 'Administrator-Konto wird nicht überschrieben',
            items: [{
              message: `Dieser VARIO-Kunde zeigt auf das Gambio-Konto ${result.gambioCustomerId ?? '?'}, `
                + 'das im Shop ein Administrator-Konto ist. Ein Export würde dessen Kundengruppe und '
                + 'Login-E-Mail überschreiben und den Shop-Zugang gefährden — er wurde deshalb abgebrochen, '
                + 'ohne etwas zu ändern.',
            }, {
              message: 'Administrator-Konten werden ausschließlich im Gambio-Backend gepflegt. '
                + 'Ist die Verknüpfung falsch, muss sie am VARIO-Konto entfernt werden — häufigste Ursache '
                + 'ist eine E-Mail-Adresse, die der eines Administrators entspricht.',
            }],
            context: { action: rpc, accountId, salesChannelId, kind, jobId },
            json: job.result,
          });
        } else if (result.rejected === 'no-customer-group-mapping') {
          const varioGroup = result.varioCustomerGroupLabel ?? result.varioPriceGroupLabel ?? null;
          openErrorModal({
            title: 'Kunde ist keiner Gambio-Kundengruppe zugeordnet',
            items: [{
              message: varioGroup
                ? `Die VARIO-Gruppe „${varioGroup}" dieses Kunden ist keiner Gambio-Kundengruppe zugeordnet. `
                  + 'Exportiert werden nur Kunden aus zugeordneten Kundengruppen.'
                : 'Dieser Kunde hat weder eine VARIO-Kundengruppe noch eine Preisgruppe, über die eine '
                  + 'Gambio-Kundengruppe zugeordnet werden könnte. Exportiert werden nur Kunden aus zugeordneten Kundengruppen.',
            }, {
              message: 'Zuordnung pflegen: Verkaufskanal → Einstellungen → Kundengruppen.',
            }],
            context: { action: rpc, accountId, salesChannelId, kind, jobId },
            json: job.result,
          });
        } else if (job.status === 'failed') {
          openErrorModal({
            title: 'Export fehlgeschlagen',
            items: [{ message: job.error ?? 'Unbekannter Fehler' }],
            context: { action: rpc, accountId, salesChannelId, kind, jobId },
            json: job,
          });
        } else if (job.status === 'completed' && !result.rejected) {
          showToast('Export abgeschlossen', 'success');
        }
      } catch {
        // Timeout o. Ä. — der Job läuft weiter, das Ergebnis steht im Log-Tab.
        showToast('Export läuft noch — Ergebnis steht im Log.', 'info');
      } finally {
        btn.disabled = false;
      }
    }

    // Status neu laden
    await renderStatus();
  }
}

// ── Markup + Utils ─────────────────────────────────────────────────────────

function renderShell(): string {
  return `
    <style>
      /* Inline-Slot autogrow: bricht die bridge.css-Kette (html/body/main#app height:100%) auf,
         damit body.scrollHeight = Inhalt und das iframe auf den Inhalt wächst. */
      html.go-lister-autogrow, html.go-lister-autogrow body, html.go-lister-autogrow main#app { height:auto; overflow:visible; }
      html.go-lister-autogrow main#app > section#content { flex:0 0 auto; }
      .go-acct-export .apphead { display:flex; align-items:center; gap:14px; }
      .go-acct-export .brand { display:inline-flex; align-items:center; text-decoration:none; flex:none; }
      .go-acct-export .brand img { height:20px; display:block; }
      .go-acct-export .gambio-logo { height:30px; width:auto; flex:none; mix-blend-mode:multiply; }
      .go-acct-export .hdivider { width:1px; height:24px; background:var(--border); flex:none; }
      .go-acct-export .heyebrow { font-size:10.5px; font-weight:600; letter-spacing:.11em; text-transform:uppercase; color:var(--muted); }
      .go-acct-export .htitle h1 { font-size:20px; font-weight:600; letter-spacing:-0.01em; margin:0; line-height:1.2; }
      .go-acct-export .hactions { margin-left:auto; display:flex; gap:13px; align-items:center; }
    </style>
    <div class="kf-page go-acct-export" style="padding:10px;">
      <header class="apphead" style="margin-bottom:8px;">
        <a class="brand" href="https://www.go-ecommerce.de" target="_blank" rel="noopener" title="go eCommerce">
          <img src="/logo-black.png" alt="go eCommerce" />
        </a>
        <img class="gambio-logo" src="/gambio-logo.png?v=2" alt="Gambio" />
        <span class="hdivider"></span>
        <div class="htitle"><div class="heyebrow">Gambio · Export</div><h1>In Gambio anlegen</h1></div>
        <div class="hactions">${helpButtonHtml('kunden-hersteller-export')}<span id="slot-status"></span></div>
      </header>
      <div id="slot-body"></div>
    </div>`;
}

function readSlotParams(): { accountId: string | null } {
  const q = new URL(window.location.href).searchParams;
  const accountId = q.get('id') ?? q.get('accountId') ?? q.get('account_id');
  return { accountId };
}

function setStatusBar(host: HTMLElement, kind: 'loading' | 'success' | 'error' | '', msg: string): void {
  const el = host.querySelector<HTMLElement>('#slot-status');
  if (!el) return;
  if (!kind) { el.innerHTML = ''; return; }
  const icon = kind === 'loading' ? 'fa-spinner-third fa-spin' : kind === 'success' ? 'fa-circle-check' : 'fa-circle-xmark';
  const cls = kind === 'success' ? 'v-text-success' : kind === 'error' ? 'v-text-error' : 'v-text-muted';
  el.innerHTML = `<span class="${cls}" style="font-size:0.88em;"><span class="v-icon fa-regular ${icon}"></span> ${escapeHtml(msg)}</span>`;
}

function escapeHtml(s: string | null | undefined): string {
  if (s === null || s === undefined) return '';
  return String(s)
    .replaceAll('&', '&amp;')
    .replaceAll('<', '&lt;')
    .replaceAll('>', '&gt;')
    .replaceAll('"', '&quot;')
    .replaceAll("'", '&#39;');
}
